import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Select, MenuItem, Tooltip, IconButton, Icon } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Trash from '../../common/trash';
import DownloadExcel from '../../common/download-excel';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'flex-end'
    }
}));

const Suppliers = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [excelData, setExcelData] = useState([]);
    const [state, setState] = useState({
        loading: true,
        suppliers: [],
        supplier: {
            idsupplier: 0,
            code: '',
            name: '',
            avoid_fees: false
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Suppliers';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/suppliers');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, suppliers: response?.data ?? [] }));
        } else {
            console.log('[Suppliers] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, supplier: { ...prev.supplier, [target.name]: target.value } }));

    const validateForm = (type) => {
        const { idsupplier, name } = state.supplier;
        const { create_attr, update_attr, delete_attr } = state.permissions;

        switch (type) {
            case 'update':
                if (!update_attr) {
                    showToast('You do not have permissions for this action', 'error', 6500);
                    return false;
                }
                const supplier = state.suppliers.find(value => value.idsupplier === idsupplier);
                if (!supplier) {
                    showToast('Select some supplier', 'error');
                    return false;
                }
                const before = JSON.stringify(supplier);
                const after = JSON.stringify(state.supplier);
                if (before === after) {
                    showToast('Update some field', 'error');
                    return false;
                }
                if (!name.trim()) {
                    showToast('All fields are required', 'error');
                    return false;
                }

                return true;

            case 'store':
                if (!create_attr) {
                    showToast('You do not have permissions for this action', 'error', 6500);
                    return false;
                }
                if (!name.trim()) {
                    showToast('All fields are required', 'error');
                    return false;
                }

                return true;

            case 'delete':
                if (!delete_attr) {
                    showToast('You do not have permissions for this action', 'error', 6500);
                    return false;
                }
                if (idsupplier === 0) {
                    showToast('Select some supplier', 'error');
                    return false;
                }

                return window.confirm('Are you sure you want to delete this supplier?');

            default:
                break;
        }
    }

    const clear = () => setState(prev => ({ ...prev, supplier: { ...prev.supplier, idsupplier: 0, code: '', name: '', avoid_fees: false } }));

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/supplier/store', state.supplier);
            if (response.status === 200) {
                setState(prev => ({ ...prev, suppliers: response?.data ?? prev.suppliers }));
                showToast(response.message);
            } else {
                console.log('[Suppliers] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { idsupplier } = state.supplier;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/supplier/${idsupplier}/update`, state.supplier);
            if (response.status === 200) {
                setState(prev => ({ ...prev, suppliers: response?.data ?? prev.suppliers }));
                showToast(response.message);
            } else {
                console.log('[Suppliers] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idsupplier } = state.supplier;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/supplier/${idsupplier}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, suppliers: response?.data ?? prev.suppliers }));
                showToast(response.message);
            } else {
                console.log('[Suppliers] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const getToDownload = async () => {
        showLoader(true);
        const response = await get(`/supplier/getToDownload`);
        if (response.status === 200) {
            setExcelData(response.data);

            if (!response?.data?.length) showToast('There is no data to download', 'warning');
        } else {
            console.log('[Suppliers] getToDownload error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const valueGetter = params => params.value ? 'Yes' : 'No';

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/suppliers/${row.idsupplier}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 1, minWidth: 150 },
        { field: 'name', headerName: 'Name', flex: 2, minWidth: 240 },
        { field: 'avoid_fees', headerName: 'Avoid fees', flex: 1, minWidth: 150, valueGetter },
        { field: 'actions', headerName: 'Actions', flex: 0.5, renderCell }
    ];

    const excelColumns = [
        { label: 'Code', value: 'code' },
        { label: 'Name', value: 'name' },
        { label: 'Avoid fees', value: 'avoid_fees' },
        { label: 'Sede code', value: 'sede.code' },
        { label: 'Sede name', value: 'sede.name' }
    ];

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr) return <NotFound />;

    return (
        <div className={classes.root}>
            <ViewTitle sedeFlag title='Suppliers' />
            <div>
                <Grid container>
                    <Grid item xs={12} md={6} lg={4}>
                        <FormGroup>
                            <FormLabel>{'Code'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    disabled
                                    placeholder={'Code (automatic)'}
                                    autoComplete='off'
                                    type='text'
                                    name='code'
                                    value={state.supplier.code}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.supplier.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <FormGroup>
                            <FormLabel>{'Avoid fees'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    value={state.supplier.avoid_fees}
                                    name='avoid_fees'
                                    onChange={handleChange}
                                >
                                    <MenuItem value={false}>{'No'}</MenuItem>
                                    <MenuItem value={true}>{'Yes'}</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            <div className={classes.buttonsContainer}>
                <DownloadExcel margin fileName='suppliers-report' sheetName='Suppliers' data={excelData} columns={excelColumns} onClick={getToDownload} onReset={() => setExcelData([])} />

                {
                    state.permissions.delete_attr &&
                    <Trash table='suppliers' id='idsupplier' onRefresh={makeRequest} />
                }
            </div>

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.suppliers}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idsupplier}
                    loading={state.loading}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, supplier: row }))}
                />
            </div>
        </div>
    );
}

export default Suppliers;